import { ISqueezeChartData } from 'interfaces/timeline';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { generateTranslationIdWithValues } from 'translations/utils/translationGenerator';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';
import { formatDate } from 'helpers/dates';
import SqueezeStrengthChartLegend from './SqueezeStrengthChartLegend';
import SqueezeStrengthChartTooltip from './SqueezeStrengthChartTooltip';
import { Button } from 'reactstrap';
import { ZoomModeConfig } from '../AssessmentFormChart/AssessmentFormChart';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrows } from '@fortawesome/pro-solid-svg-icons';
import { TrendVisualizationChartType } from 'interfaces/trendVisualizations';
import useXAxisFormatting from 'hooks/useXAxisFormatting';
import useChartZoom from 'hooks/useChartZoom';

type SqueezeStrengthChartProps = {
  squeezeData: ISqueezeChartData;
  chartType: TrendVisualizationChartType;
};

export enum SqueezeStrengthType {
  MIN_STRENGTH = 'MinSqueezesStrength',
  AVG_STRENGTH = 'AvgSqueezesStrength',
  MAX_STRENGTH = 'MaxSqueezesStrength',
}

export enum SqueezeStrengthTypeColor {
  MIN_STRENGTH = '#237fc033',
  AVG_STRENGTH = '#237fc099',
  MAX_STRENGTH = '#237fc0',
}

export const predefinedChartModes = generateTranslationIdWithValues(
  'UserDashboard.SqueezeSummary.modes',
  [
    SqueezeStrengthType.MIN_STRENGTH,
    SqueezeStrengthType.AVG_STRENGTH,
    SqueezeStrengthType.MAX_STRENGTH,
  ]
);

const initialZoomModeConfig: ZoomModeConfig = {
  left: undefined,
  right: undefined,
  refAreaLeft: undefined,
  refAreaRight: undefined,
  animation: true,
};

const SqueezeStrengthChart = ({
  squeezeData,
  chartType,
}: SqueezeStrengthChartProps) => {
  const intl = useIntl();

  const [selectedSqueezeStrengthType, setSelectedSqueezeStrengthType] =
    useState<SqueezeStrengthType>(SqueezeStrengthType.AVG_STRENGTH);

  const selectedSqueezeStrengthTypeTranslationId = useMemo(() => {
    return (
      predefinedChartModes.find((p) => p.value === selectedSqueezeStrengthType)
        ?.translationId ?? ''
    );
  }, [selectedSqueezeStrengthType]);

  const formattedChartData = squeezeData.x_axis.map((date, index) => ({
    name: formatDate(date, {
      addSeconds: false,
    }),
    minSqueezesStrength: squeezeData.series.find(
      (p) => p.mode === SqueezeStrengthType.MIN_STRENGTH
    )!.data[index],
    maxSqueezesStrength: squeezeData.series.find(
      (p) => p.mode === SqueezeStrengthType.MAX_STRENGTH
    )!.data[index],
    avgSqueezesStrength: squeezeData.series.find(
      (p) => p.mode === SqueezeStrengthType.AVG_STRENGTH
    )!.data[index],
  }));

  const formattingFunction = useXAxisFormatting(squeezeData.x_axis);

  const handleTickFormatting = (value: any) => formattingFunction(value);

  const renderSqueezeStrengthChartTooltip = (
    props: TooltipProps<string, string>
  ) => {
    return (
      <SqueezeStrengthChartTooltip
        {...props}
        translationId={selectedSqueezeStrengthTypeTranslationId}
      />
    );
  };

  const renderBars = useMemo(() => {
    return Object.entries(SqueezeStrengthType)
      .filter(([, value]) => value === selectedSqueezeStrengthType)
      .map(([key, value], index) => (
        <Bar
          key={key}
          dataKey={`${value[0].toLocaleLowerCase()}${value.substring(1)}`}
          name={intl.formatMessage({
            id: predefinedChartModes[index].translationId,
          })}
          fill={SqueezeStrengthTypeColor[key]}
          yAxisId="1"
        />
      ));
  }, [selectedSqueezeStrengthType, intl]);

  const renderLines = useMemo(() => {
    return Object.entries(SqueezeStrengthType)
      .filter(([, value]) => value === selectedSqueezeStrengthType)
      .map(([key, value], index) => (
        <Line
          key={key}
          dataKey={`${value[0].toLocaleLowerCase()}${value.substring(1)}`}
          name={intl.formatMessage({
            id: predefinedChartModes[index].translationId,
          })}
          fill={SqueezeStrengthTypeColor[key]}
          yAxisId="1"
        />
      ));
  }, [selectedSqueezeStrengthType, intl]);

  const {
    zoomModeConfig,
    handleMouseDown,
    handleMouseMove,
    handleResetZoom,
    zoomHandler,
  } = useChartZoom(initialZoomModeConfig);

  const zoom = () => zoomHandler(formattedChartData);

  return (
    <div className="d-flex flex-column" style={{ userSelect: 'none' }}>
      {zoomModeConfig.left !== undefined && zoomModeConfig.right !== undefined && (
        <Button
          color="primary"
          outline
          className="me-3 align-self-end"
          onClick={handleResetZoom}
        >
          <FontAwesomeIcon icon={faArrows} className="me-1" />
          <FormattedMessage id="TimelineDetails.TrendsTimeline.zoomOut" />
        </Button>
      )}
      <ResponsiveContainer
        className="SqueezeStrengthChart"
        width={'99%'}
        height={400}
      >
        {chartType === TrendVisualizationChartType.BarChart ? (
          <BarChart
            data={
              zoomModeConfig.left && zoomModeConfig.right
                ? formattedChartData.slice(
                    zoomModeConfig.left,
                    zoomModeConfig.right
                  )
                : formattedChartData
            }
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={zoom}
            margin={{
              left: 5,
              bottom: 5,
              right: 5,
              top: 25,
            }}
          >
            <CartesianGrid strokeDasharray="1 1" vertical={false} />
            <XAxis
              dataKey="name"
              tickFormatter={handleTickFormatting}
              minTickGap={0}
              tickMargin={15}
              tickCount={squeezeData.x_axis.length}
            />
            <YAxis allowDataOverflow={false} yAxisId="1" />
            <Tooltip
              content={renderSqueezeStrengthChartTooltip}
              cursor={{ fill: '#e0e0e0' }}
            />
            <Legend
              wrapperStyle={{ paddingTop: '20px' }}
              content={
                <SqueezeStrengthChartLegend
                  selectedSqueezeStrengthType={selectedSqueezeStrengthType}
                  setSelectedSqueezeStrengthType={
                    setSelectedSqueezeStrengthType
                  }
                />
              }
            />
            {renderBars}
            {zoomModeConfig.refAreaLeft && zoomModeConfig.refAreaRight ? (
              <ReferenceArea
                yAxisId="1"
                x1={zoomModeConfig.refAreaLeft}
                x2={zoomModeConfig.refAreaRight}
                strokeOpacity={0.3}
              />
            ) : null}
          </BarChart>
        ) : (
          <LineChart
            data={
              zoomModeConfig.left && zoomModeConfig.right
                ? formattedChartData.slice(
                    zoomModeConfig.left,
                    zoomModeConfig.right
                  )
                : formattedChartData
            }
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={zoom}
            margin={{
              left: 5,
              bottom: 5,
              right: 5,
              top: 25,
            }}
          >
            <CartesianGrid strokeDasharray="1 1" vertical={false} />
            <XAxis
              dataKey="name"
              tickFormatter={handleTickFormatting}
              tickMargin={15}
              minTickGap={0}
              tickCount={squeezeData.x_axis.length}
            />
            <YAxis allowDataOverflow={false} yAxisId="1" />
            <Tooltip
              content={renderSqueezeStrengthChartTooltip}
              cursor={{ fill: '#e0e0e0' }}
            />
            <Legend
              wrapperStyle={{ paddingTop: '20px' }}
              content={
                <SqueezeStrengthChartLegend
                  selectedSqueezeStrengthType={selectedSqueezeStrengthType}
                  setSelectedSqueezeStrengthType={
                    setSelectedSqueezeStrengthType
                  }
                />
              }
            />
            {renderLines}
            {zoomModeConfig.refAreaLeft && zoomModeConfig.refAreaRight ? (
              <ReferenceArea
                yAxisId="1"
                x1={zoomModeConfig.refAreaLeft}
                x2={zoomModeConfig.refAreaRight}
                strokeOpacity={0.3}
              />
            ) : null}
          </LineChart>
        )}
      </ResponsiveContainer>
    </div>
  );
};

export default SqueezeStrengthChart;
